/**
 * Sistema de Pagos para Risko Pulse
 * 
 * Maneja el registro de pagos en Firestore y la comunicación con el backend
 */

import { db } from './firebase'
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore'

export type PaymentStatus = 'pending' | 'confirmed' | 'failed' | 'expired'

export interface Payment {
  id: string
  userId: string
  userEmail: string
  plan: {
    name: string
    price: number
    duration: string
  }
  paymentData: {
    currency: string
    address: string
    orderId: string
  }
  status: PaymentStatus
  createdAt: number
  confirmedAt?: number
  expiresAt: number // 15 minutos después de creado
}

/**
 * Registra un nuevo pago en Firestore
 */
export async function createPayment(
  userId: string,
  userEmail: string,
  plan: { name: string; price: number; duration: string },
  paymentData: { currency: string; address: string; orderId: string }
): Promise<{ success: boolean; paymentId?: string; error?: string }> {
  try {
    const paymentId = `payment_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
    const createdAt = Date.now()
    const expiresAt = createdAt + (15 * 60 * 1000) // 15 minutos

    const payment: Payment = {
      id: paymentId,
      userId,
      userEmail,
      plan,
      paymentData,
      status: 'pending',
      createdAt,
      expiresAt
    }

    if (!db) {
      return {
        success: false,
        error: 'Firestore no está inicializado'
      }
    }
    // Guardar en Firestore
    await setDoc(doc(db, 'payments', paymentId), {
      ...payment,
      createdAt: serverTimestamp(),
      expiresAt: new Date(expiresAt)
    })

    return {
      success: true,
      paymentId
    }
  } catch (error) {
    console.error('Error creating payment:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error al crear pago'
    }
  }
}

/**
 * Obtiene un pago por ID
 */
export async function getPayment(paymentId: string): Promise<Payment | null> {
  try {
    if (!db) {
      return null
    }
    const docRef = doc(db, 'payments', paymentId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return null
    }

    const data = docSnap.data()
    return {
      ...data,
      createdAt: data.createdAt?.toMillis() || Date.now(),
      expiresAt: data.expiresAt?.toMillis() || Date.now(),
      confirmedAt: data.confirmedAt?.toMillis()
    } as Payment
  } catch (error) {
    console.error('Error getting payment:', error)
    return null
  }
}

/**
 * Verifica si un pago ha expirado
 */
export function isPaymentExpired(payment: Payment): boolean {
  return Date.now() > payment.expiresAt
}

